import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaUser, FaBriefcase, FaGraduationCap, FaMapMarker, FaHeart, FaUsers, FaTree, FaEdit, FaPrint, FaClock, FaStar, FaBirthdayCake, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import EditUserModal from './EditUserModal';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const AdminUserDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [editSection, setEditSection] = useState('');
    const [generatingPDF, setGeneratingPDF] = useState(false);

    useEffect(() => {
        fetchUserDetails();
    }, [id]);

    const fetchUserDetails = async () => {
        try {
            const token = localStorage.getItem('adminToken');
            const response = await axios.get(`https://app.bihebari.com.np/api/admin/users/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            if (response.data.success) {
                setUser(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching user:', error);
            toast.error('Error loading user details');
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (section) => {
        setEditSection(section);
        setModalOpen(true);
    };

    // Format height from stored format (e.g., "5ft 05in" or "5'05\"")
    const formatHeight = (height) => {
        if (!height) return 'Not specified';
        
        if (height.includes("'")) {
            return height;
        }
        
        if (height.includes('ft') && height.includes('in')) {
            const feetMatch = height.match(/(\d+)ft/);
            const inchesMatch = height.match(/(\d+)in/);
            
            if (feetMatch && inchesMatch) {
                const feet = feetMatch[1];
                const inches = inchesMatch[1];
                return `${feet}'${inches}"`;
            }
        }
        
        const numHeight = parseFloat(height);
        if (!isNaN(numHeight) && numHeight > 0) {
            const feet = Math.floor(numHeight / 12);
            const inches = Math.round(numHeight % 12);
            return `${feet}'${inches}"`;
        }
        
        return height;
    };

    // Format weight (if stored with unit)
    const formatWeight = (weight) => {
        if (!weight) return 'Not specified';
        if (typeof weight === 'string' && (weight.includes('kg') || weight.includes('KG'))) {
            return weight;
        }
        const numWeight = parseFloat(weight);
        if (!isNaN(numWeight)) {
            return `${numWeight} kg`;
        }
        return weight;
    };

    // Calculate age safely
    const calculateAge = (dob) => {
        if (!dob) return 'N/A';
        try {
            const birthDate = new Date(dob);
            const today = new Date();
            let age = today.getFullYear() - birthDate.getFullYear();
            const monthDiff = today.getMonth() - birthDate.getMonth();
            if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
                age--;
            }
            return age;
        } catch (error) {
            return 'N/A';
        }
    };

    // Helper function to get country name from ID
    const getCountryName = (countryId) => {
        const countries = {
            '153': 'Nepal',
            '1': 'USA',
            '91': 'India',
            '44': 'UK',
            '61': 'Australia',
            '86': 'China',
            '81': 'Japan',
            '92': 'Pakistan',
            '94': 'Sri Lanka',
            '880': 'Bangladesh'
        };
        return countries[countryId] || countryId || 'Not specified';
    };

    const generateUserPDF = async () => {
        setGeneratingPDF(true);
        
        const tempDiv = document.createElement('div');
        tempDiv.style.position = 'absolute';
        tempDiv.style.top = '-9999px';
        tempDiv.style.left = '-9999px';
        tempDiv.style.width = '800px';
        tempDiv.style.backgroundColor = 'white';
        tempDiv.style.padding = '40px';
        tempDiv.style.fontFamily = 'Arial, sans-serif';
        
        const currentDate = new Date().toLocaleString();
        
        tempDiv.innerHTML = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="UTF-8">
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: 'Arial', sans-serif;
                        line-height: 1.5;
                        color: #333;
                    }
                    .container {
                        max-width: 100%;
                        margin: 0 auto;
                    }
                    .header {
                        background: linear-gradient(135deg, #dc2626, #1e3a8a);
                        padding: 30px;
                        text-align: center;
                        color: white;
                        margin-bottom: 30px;
                    }
                    .header h1 {
                        font-size: 28px;
                        margin-bottom: 10px;
                    }
                    .header p {
                        font-size: 14px;
                    }
                    .member-id {
                        background: rgba(255,255,255,0.2);
                        padding: 5px 15px;
                        border-radius: 20px;
                        display: inline-block;
                        margin-top: 10px;
                    }
                    .section {
                        margin-bottom: 25px;
                        break-inside: avoid;
                    }
                    .section-title {
                        background: linear-gradient(135deg, #dc2626, #1e3a8a);
                        color: white;
                        padding: 10px 15px;
                        border-radius: 8px;
                        margin-bottom: 15px;
                        font-size: 18px;
                        font-weight: bold;
                    }
                    .info-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 15px;
                        padding: 15px;
                        background: #f7fafc;
                        border-radius: 8px;
                    }
                    .info-item {
                        display: flex;
                        align-items: baseline;
                    }
                    .info-label {
                        font-weight: bold;
                        width: 180px;
                        color: #4a5568;
                    }
                    .info-value {
                        color: #2d3748;
                        flex: 1;
                    }
                    .full-width {
                        grid-column: span 2;
                    }
                    .footer {
                        text-align: center;
                        margin-top: 30px;
                        padding-top: 20px;
                        border-top: 1px solid #e2e8f0;
                        color: #718096;
                        font-size: 12px;
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <div class="header">
                        <h1>Bihebari Matrimony</h1>
                        <p>Complete Profile Information</p>
                        <div class="member-id">Member ID: BB${user?.id}</div>
                    </div>
                    
                    <!-- Personal Information -->
                    <div class="section">
                        <div class="section-title">📋 Personal Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Full Name:</span><span class="info-value">${user?.name || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Gender:</span><span class="info-value">${user?.gender || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Date of Birth:</span><span class="info-value">${user?.dob || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Birth Time:</span><span class="info-value">${user?.birth_time || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Age:</span><span class="info-value">${calculateAge(user?.dob)} years</span></div>
                            <div class="info-item"><span class="info-label">Birth Country:</span><span class="info-value">${getCountryName(user?.birth_location) || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Birth State:</span><span class="info-value">${user?.birth_state_name || user?.birth_state || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Birth City:</span><span class="info-value">${user?.birth_city_name || user?.birth_city || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Horoscope/Rashi:</span><span class="info-value">${user?.horscope || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Caste:</span><span class="info-value">${user?.caste || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Gotra:</span><span class="info-value">${user?.gotra || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Marital Status:</span><span class="info-value">${user?.marital || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Children:</span><span class="info-value">${user?.children || 0}</span></div>
                            <div class="info-item"><span class="info-label">Children Status:</span><span class="info-value">${user?.children_status || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Food Preference:</span><span class="info-value">${user?.eat_status || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Mother's Surname:</span><span class="info-value">${user?.moms_surname || 'Not specified'}</span></div>
                            <div class="info-item full-width"><span class="info-label">About Me:</span><span class="info-value">${user?.about_me || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Contact Information -->
                    <div class="section">
                        <div class="section-title">📞 Contact Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Email:</span><span class="info-value">${user?.email || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Mobile Number:</span><span class="info-value">${user?.contact || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Country:</span><span class="info-value">${user?.country_name || user?.country || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">State:</span><span class="info-value">${user?.state_name || user?.state || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">City:</span><span class="info-value">${user?.city_name || user?.city || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">District:</span><span class="info-value">${user?.distric || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Residence Status:</span><span class="info-value">${user?.residence_status || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Career Information -->
                    <div class="section">
                        <div class="section-title">💼 Career Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Education:</span><span class="info-value">${user?.education || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Occupation:</span><span class="info-value">${user?.occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Monthly Income:</span><span class="info-value">${user?.monthaly_income ? '₹' + user.monthaly_income : 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Company Name:</span><span class="info-value">${user?.employee_in || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Designation:</span><span class="info-value">${user?.designation || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Family Information -->
                    <div class="section">
                        <div class="section-title">👨‍👩‍👧‍👦 Family Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Father's Name:</span><span class="info-value">${user?.father || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Father's Occupation:</span><span class="info-value">${user?.father_occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Mother's Name:</span><span class="info-value">${user?.mother || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Mother's Occupation:</span><span class="info-value">${user?.mother_occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Unmarried Sisters:</span><span class="info-value">${user?.unmarride_sister || 0}</span></div>
                            <div class="info-item"><span class="info-label">Married Sisters:</span><span class="info-value">${user?.marride_sister || 0}</span></div>
                            <div class="info-item"><span class="info-label">Unmarried Brothers:</span><span class="info-value">${user?.unmarride_brother || 0}</span></div>
                            <div class="info-item"><span class="info-label">Married Brothers:</span><span class="info-value">${user?.marride_brother || 0}</span></div>
                        </div>
                    </div>
                    
                    <!-- Partner Preferences -->
                    <div class="section">
                        <div class="section-title">💑 Partner Preferences</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Looking For:</span><span class="info-value">${user?.looking_for || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Partner's Marital Status:</span><span class="info-value">${user?.marit || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Age Gap:</span><span class="info-value">${user?.age || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Partner's Education:</span><span class="info-value">${user?.partner_education || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Partner's Occupation:</span><span class="info-value">${user?.partner_occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Preferred Location:</span><span class="info-value">${user?.location || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Appearance Information -->
                    <div class="section">
                        <div class="section-title">✨ Appearance</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Height:</span><span class="info-value">${formatHeight(user?.height)}</span></div>
                            <div class="info-item"><span class="info-label">Weight:</span><span class="info-value">${formatWeight(user?.weight)}</span></div>
                            <div class="info-item"><span class="info-label">Skin Tone:</span><span class="info-value">${user?.skin_tone || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Payment Information -->
                    <div class="section">
                        <div class="section-title">💰 Payment Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Registration Amount:</span><span class="info-value">₹${user?.amount || 5000}</span></div>
                            <div class="info-item"><span class="info-label">Payment Status:</span><span class="info-value">${user?.pay_status === 'paid' ? '✓ Paid' : '⏳ Pending'}</span></div>
                            <div class="info-item"><span class="info-label">Registered On:</span><span class="info-value">${user?.created ? new Date(user.created).toLocaleString() : 'N/A'}</span></div>
                        </div>
                    </div>
                    
                    <div class="footer">
                        <p>Generated on: ${currentDate}</p>
                        <p>Bihebari Matrimony - Find Your Perfect Match</p>
                    </div>
                </div>
            </body>
            </html>
        `;
        
        document.body.appendChild(tempDiv);
        
        try {
            const canvas = await html2canvas(tempDiv, {
                scale: 2,
                logging: false,
                useCORS: true,
                backgroundColor: '#ffffff'
            });
            
            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4'
            });
            
            const imgWidth = 210;
            const pageHeight = 297;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;
            let heightLeft = imgHeight;
            let position = 0;
            
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
            
            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }
            
            pdf.save(`User_Profile_BB${user?.id}_${user?.name?.replace(/\s/g, '_') || 'User'}.pdf`);
            toast.success('Profile downloaded successfully!');
        } catch (error) {
            console.error('PDF generation error:', error);
            toast.error('Failed to generate PDF. Please try again.');
        } finally {
            document.body.removeChild(tempDiv);
            setGeneratingPDF(false);
        }
    };

    const handlePrint = () => {
        const printWindow = window.open('', '_blank');
        printWindow.document.write(`
            <!DOCTYPE html>
            <html>
            <head>
                <title>User Profile - ${user?.name}</title>
                <style>
                    * {
                        margin: 0;
                        padding: 0;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
                        padding: 40px;
                        background: white;
                        color: #333;
                    }
                    .container {
                        max-width: 1200px;
                        margin: 0 auto;
                    }
                    .header {
                        text-align: center;
                        margin-bottom: 30px;
                        padding-bottom: 20px;
                        border-bottom: 3px solid #e53e3e;
                    }
                    .header h1 {
                        color: #e53e3e;
                        font-size: 28px;
                        margin-bottom: 5px;
                    }
                    .header p {
                        color: #666;
                        font-size: 14px;
                    }
                    .member-id {
                        background: linear-gradient(135deg, #e53e3e, #3182ce);
                        color: white;
                        padding: 5px 15px;
                        border-radius: 20px;
                        display: inline-block;
                        margin-top: 10px;
                    }
                    .section {
                        margin-bottom: 25px;
                        break-inside: avoid;
                    }
                    .section-title {
                        background: linear-gradient(135deg, #e53e3e, #3182ce);
                        color: white;
                        padding: 10px 15px;
                        border-radius: 8px;
                        margin-bottom: 15px;
                        font-size: 18px;
                        font-weight: bold;
                    }
                    .info-grid {
                        display: grid;
                        grid-template-columns: repeat(2, 1fr);
                        gap: 15px;
                        padding: 15px;
                        background: #f7fafc;
                        border-radius: 8px;
                    }
                    .info-item {
                        display: flex;
                        align-items: baseline;
                    }
                    .info-label {
                        font-weight: bold;
                        width: 150px;
                        color: #4a5568;
                    }
                    .info-value {
                        color: #2d3748;
                        flex: 1;
                    }
                    .full-width {
                        grid-column: span 2;
                    }
                    .footer {
                        text-align: center;
                        margin-top: 30px;
                        padding-top: 20px;
                        border-top: 1px solid #e2e8f0;
                        color: #718096;
                        font-size: 12px;
                    }
                    @media print {
                        body {
                            padding: 20px;
                        }
                        .no-print {
                            display: none;
                        }
                        .section-title {
                            background: #e53e3e;
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                        .member-id {
                            -webkit-print-color-adjust: exact;
                            print-color-adjust: exact;
                        }
                    }
                </style>
            </head>
            <body>
                <div class="container">
                    <div class="header">
                        <h1>Bihebari Matrimony</h1>
                        <p>Complete Profile Information</p>
                        <div class="member-id">Member ID: BB${user?.id}</div>
                    </div>
                    
                    <!-- Personal Information -->
                    <div class="section">
                        <div class="section-title">📋 Personal Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Full Name:</span><span class="info-value">${user?.name || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Gender:</span><span class="info-value">${user?.gender || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Date of Birth:</span><span class="info-value">${user?.dob || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Birth Time:</span><span class="info-value">${user?.birth_time || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Age:</span><span class="info-value">${calculateAge(user?.dob)} years</span></div>
                            <div class="info-item"><span class="info-label">Birth Country:</span><span class="info-value">${getCountryName(user?.birth_location) || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Birth State:</span><span class="info-value">${user?.birth_state_name || user?.birth_state || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Birth City:</span><span class="info-value">${user?.birth_city_name || user?.birth_city || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Horoscope/Rashi:</span><span class="info-value">${user?.horscope || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Caste:</span><span class="info-value">${user?.caste || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Gotra:</span><span class="info-value">${user?.gotra || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Marital Status:</span><span class="info-value">${user?.marital || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Children:</span><span class="info-value">${user?.children || 0}</span></div>
                            <div class="info-item"><span class="info-label">Children Status:</span><span class="info-value">${user?.children_status || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Food Preference:</span><span class="info-value">${user?.eat_status || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Mother's Surname:</span><span class="info-value">${user?.moms_surname || 'Not specified'}</span></div>
                            <div class="info-item full-width"><span class="info-label">About Me:</span><span class="info-value">${user?.about_me || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Contact Information -->
                    <div class="section">
                        <div class="section-title">📞 Contact Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Email:</span><span class="info-value">${user?.email || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Mobile Number:</span><span class="info-value">${user?.contact || 'Not provided'}</span></div>
                            <div class="info-item"><span class="info-label">Country:</span><span class="info-value">${user?.country_name || user?.country || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">State:</span><span class="info-value">${user?.state_name || user?.state || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">City:</span><span class="info-value">${user?.city_name || user?.city || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">District:</span><span class="info-value">${user?.distric || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Residence Status:</span><span class="info-value">${user?.residence_status || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Career Information -->
                    <div class="section">
                        <div class="section-title">💼 Career Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Education:</span><span class="info-value">${user?.education || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Occupation:</span><span class="info-value">${user?.occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Monthly Income:</span><span class="info-value">${user?.monthaly_income ? '₹' + user.monthaly_income : 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Company Name:</span><span class="info-value">${user?.employee_in || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Designation:</span><span class="info-value">${user?.designation || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Family Information -->
                    <div class="section">
                        <div class="section-title">👨‍👩‍👧‍👦 Family Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Father's Name:</span><span class="info-value">${user?.father || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Father's Occupation:</span><span class="info-value">${user?.father_occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Mother's Name:</span><span class="info-value">${user?.mother || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Mother's Occupation:</span><span class="info-value">${user?.mother_occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Unmarried Sisters:</span><span class="info-value">${user?.unmarride_sister || 0}</span></div>
                            <div class="info-item"><span class="info-label">Married Sisters:</span><span class="info-value">${user?.marride_sister || 0}</span></div>
                            <div class="info-item"><span class="info-label">Unmarried Brothers:</span><span class="info-value">${user?.unmarride_brother || 0}</span></div>
                            <div class="info-item"><span class="info-label">Married Brothers:</span><span class="info-value">${user?.marride_brother || 0}</span></div>
                        </div>
                    </div>
                    
                    <!-- Partner Preferences -->
                    <div class="section">
                        <div class="section-title">💑 Partner Preferences</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Looking For:</span><span class="info-value">${user?.looking_for || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Partner's Marital Status:</span><span class="info-value">${user?.marit || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Age Gap:</span><span class="info-value">${user?.age || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Partner's Education:</span><span class="info-value">${user?.partner_education || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Partner's Occupation:</span><span class="info-value">${user?.partner_occupation || 'Not specified'}</span></div>
                            <div class="info-item"><span class="info-label">Preferred Location:</span><span class="info-value">${user?.location || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Appearance Information -->
                    <div class="section">
                        <div class="section-title">✨ Appearance</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Height:</span><span class="info-value">${formatHeight(user?.height)}</span></div>
                            <div class="info-item"><span class="info-label">Weight:</span><span class="info-value">${formatWeight(user?.weight)}</span></div>
                            <div class="info-item"><span class="info-label">Skin Tone:</span><span class="info-value">${user?.skin_tone || 'Not specified'}</span></div>
                        </div>
                    </div>
                    
                    <!-- Payment Information -->
                    <div class="section">
                        <div class="section-title">💰 Payment Information</div>
                        <div class="info-grid">
                            <div class="info-item"><span class="info-label">Registration Amount:</span><span class="info-value">₹${user?.amount || 5000}</span></div>
                            <div class="info-item"><span class="info-label">Payment Status:</span><span class="info-value">${user?.pay_status === 'paid' ? '✓ Paid' : '⏳ Pending'}</span></div>
                            <div class="info-item"><span class="info-label">Registered On:</span><span class="info-value">${user?.created ? new Date(user.created).toLocaleString() : 'N/A'}</span></div>
                        </div>
                    </div>
                    
                    <div class="footer">
                        <p>Generated on: ${new Date().toLocaleString()}</p>
                        <p>Bihebari Matrimony - Find Your Perfect Match</p>
                    </div>
                </div>
                <script>
                    window.print();
                </script>
            </body>
            </html>
        `);
        printWindow.document.close();
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-96">
                <div className="text-center">
                    <div className="w-12 h-12 border-t-2 border-indigo-600 rounded-full animate-spin mx-auto"></div>
                    <p className="mt-2 text-gray-600">Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="text-center py-12">
                <p className="text-gray-600">User not found</p>
                <button onClick={() => navigate('/admin/users')} className="btn-primary mt-4">
                    Back to Users
                </button>
            </div>
        );
    }

    return (
        <div>
            {/* Header with Print and Download Buttons */}
            <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
                <button onClick={() => navigate('/admin/users')} className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-800">
                    <FaArrowLeft />
                    <span>Back to Users</span>
                </button>
                <div className="flex gap-3">
                    <button
                        onClick={handlePrint}
                        className="flex items-center space-x-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
                    >
                        <FaPrint />
                        <span>Print Profile</span>
                    </button>
                    <button
                        onClick={generateUserPDF}
                        disabled={generatingPDF}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition shadow-md ${
                            !generatingPDF
                                ? 'bg-red-600 hover:bg-red-700 text-white cursor-pointer' 
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        }`}
                    >
                        {generatingPDF ? (
                            <>
                                <div className="w-4 h-4 border-t-2 border-white rounded-full animate-spin"></div>
                                <span>Generating PDF...</span>
                            </>
                        ) : (
                            <>
                                <FaDownload />
                                <span>Download Profile (PDF)</span>
                            </>
                        )}
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-xl shadow-md overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-red-500 to-blue-600 p-6 text-white">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            {user.image ? (
                                <img src={`https://app.bihebari.com.np/uploads/${user.image}`} alt={user.name} className="w-20 h-20 rounded-full object-cover border-2 border-white" />
                            ) : (
                                <div className="w-20 h-20 rounded-full bg-white/20 flex items-center justify-center">
                                    <FaUser className="text-3xl" />
                                </div>
                            )}
                            <div>
                                <h1 className="text-2xl font-bold">{user.name}</h1>
                                <p className="text-white/80">Member ID: BB{user.id}</p>
                            </div>
                        </div>
                        <button onClick={() => openEditModal('personal')} className="bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg flex items-center space-x-2 transition">
                            <FaEdit />
                            <span>Edit</span>
                        </button>
                    </div>
                </div>

                <div className="p-6">
                    {/* Personal Information */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center">
                                <FaUser className="mr-2 text-indigo-600" />
                                Personal Information
                            </h2>
                            <button onClick={() => openEditModal('personal')} className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 text-sm">
                                <FaEdit />
                                <span>Edit</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><strong>Gender:</strong> {user.gender}</div>
                            <div><strong>Date of Birth:</strong> {user.dob}</div>
                            <div><strong><FaClock className="inline mr-1 text-gray-500" /> Birth Time:</strong> {user.birth_time || 'Not specified'}</div>
                            <div><strong>Age:</strong> {calculateAge(user.dob)} years</div>
                            <div><strong>Birth Country:</strong> {getCountryName(user.birth_location) || 'Not specified'}</div>
                            <div><strong>Birth State:</strong> {user.birth_state_name || user.birth_state || 'Not specified'}</div>
                            <div><strong>Birth City:</strong> {user.birth_city_name || user.birth_city || 'Not specified'}</div>
                            <div><strong>Horoscope/Rashi:</strong> {user.horscope || 'Not specified'}</div>
                            <div><strong>Caste:</strong> {user.caste || 'Not specified'}</div>
                            <div><strong>Gotra:</strong> {user.gotra || 'Not specified'}</div>
                            <div><strong>Marital Status:</strong> {user.marital}</div>
                            <div><strong>Children:</strong> {user.children || 0}</div>
                            <div><strong>Children Status:</strong> {user.children_status || 'Not specified'}</div>
                            <div><strong>Food Preference:</strong> {user.eat_status}</div>
                            <div><strong>Mother's Surname:</strong> {user.moms_surname || 'Not specified'}</div>
                            <div className="md:col-span-2"><strong>About Me:</strong> {user.about_me || 'Not specified'}</div>
                        </div>
                    </div>

                    {/* Contact Information */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Contact Information</h2>
                            <button onClick={() => openEditModal('contact')} className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 text-sm">
                                <FaEdit />
                                <span>Edit</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><strong>Email:</strong> {user.email || 'Not provided'}</div>
                            <div><strong>Mobile:</strong> {user.contact || 'Not provided'}</div>
                            <div><strong>Country:</strong> {user.country_name || user.country || 'Not specified'}</div>
                            <div><strong>State:</strong> {user.state_name || user.state || 'Not specified'}</div>
                            <div><strong>City:</strong> {user.city_name || user.city || 'Not specified'}</div>
                            <div><strong>District:</strong> {user.distric || 'Not specified'}</div>
                            <div><strong>Residence Status:</strong> {user.residence_status || 'Not specified'}</div>
                        </div>
                    </div>

                    {/* Career Information */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Career Information</h2>
                            <button onClick={() => openEditModal('education')} className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 text-sm">
                                <FaEdit />
                                <span>Edit</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><strong>Education:</strong> {user.education || 'Not specified'}</div>
                            <div><strong>Occupation:</strong> {user.occupation || 'Not specified'}</div>
                            <div><strong>Monthly Income:</strong> {user.monthaly_income || 'Not specified'}</div>
                            <div><strong>Company:</strong> {user.employee_in || 'Not specified'}</div>
                            <div><strong>Designation:</strong> {user.designation || 'Not specified'}</div>
                        </div>
                    </div>

                    {/* Family Information */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Family Information</h2>
                            <button onClick={() => openEditModal('family')} className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 text-sm">
                                <FaEdit />
                                <span>Edit</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><strong>Father's Name:</strong> {user.father || 'Not specified'}</div>
                            <div><strong>Father's Occupation:</strong> {user.father_occupation || 'Not specified'}</div>
                            <div><strong>Mother's Name:</strong> {user.mother || 'Not specified'}</div>
                            <div><strong>Mother's Occupation:</strong> {user.mother_occupation || 'Not specified'}</div>
                            <div><strong>Unmarried Sisters:</strong> {user.unmarride_sister || 0}</div>
                            <div><strong>Married Sisters:</strong> {user.marride_sister || 0}</div>
                            <div><strong>Unmarried Brothers:</strong> {user.unmarride_brother || 0}</div>
                            <div><strong>Married Brothers:</strong> {user.marride_brother || 0}</div>
                        </div>
                    </div>

                    {/* Partner Preferences */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Partner Preferences</h2>
                            <button onClick={() => openEditModal('partner')} className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 text-sm">
                                <FaEdit />
                                <span>Edit</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><strong>Looking For:</strong> {user.looking_for || 'Not specified'}</div>
                            <div><strong>Partner's Marital Status:</strong> {user.marit || 'Not specified'}</div>
                            <div><strong>Age Gap:</strong> {user.age || 'Not specified'}</div>
                            <div><strong>Partner's Education:</strong> {user.partner_education || 'Not specified'}</div>
                            <div><strong>Partner's Occupation:</strong> {user.partner_occupation || 'Not specified'}</div>
                            <div><strong>Preferred Location:</strong> {user.location || 'Not specified'}</div>
                        </div>
                    </div>

                    {/* Appearance Information */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Appearance</h2>
                            <button onClick={() => openEditModal('appearance')} className="text-indigo-600 hover:text-indigo-800 flex items-center space-x-1 text-sm">
                                <FaEdit />
                                <span>Edit</span>
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><strong>Height:</strong> {formatHeight(user.height)}</div>
                            <div><strong>Weight:</strong> {formatWeight(user.weight)}</div>
                            <div><strong>Skin Tone:</strong> {user.skin_tone || 'Not specified'}</div>
                        </div>
                    </div>

                    {/* Payment Information */}
                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-xl font-bold text-gray-800">Payment Information</h2>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><strong>Registration Amount:</strong> ₹{user.amount || 5000}</div>
                            <div><strong>Payment Status:</strong> 
                                <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                    user.pay_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                    {user.pay_status === 'paid' ? 'Paid' : 'Pending'}
                                </span>
                            </div>
                            <div><strong>Registered On:</strong> {new Date(user.created).toLocaleString()}</div>
                        </div>
                    </div>
                </div>
            </div>

            <EditUserModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                user={user}
                section={editSection}
                onUpdate={fetchUserDetails}
            />
        </div>
    );
};

export default AdminUserDetails;